import React, { useMemo } from 'react';
import './JobList.less';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { push } from 'redux-first-history';
import { useLocation, useParams } from 'react-router';
import { Trans, useTranslation } from 'react-i18next';
import queueSelectors from '../../../../redux/offline/queue.selectors';
import { applicationSelectors } from '../../../../redux/application';
import JobListItem from '../../../../components/envago/JobListItem/JobListItem';
import Icon from '../../../../components/envago/Icon/Icon';
import { searchSelectors } from '../../../../redux/search';
import { isMobilePlatform } from '../../../../helper/PlatformHelper';
import { jobsSelectors } from '../../../../redux/jobs';
import JobRowList, { JobRow } from '../../../common/JobRowList';

// Hoisted out of render — a fresh object literal would invalidate the memoised
// list items on every parent render.
const JOB_LINK_ARIA = { role: 'link' as const, tabIndex: 0 };

// Rendered height of the compact JobListItem entries (title only, no address or
// second line), used for the virtualized slot.
const COMPACT_ITEM_HEIGHT = 44;

const JobList = ({ className }: { className?: string }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const { jobId } = useParams<{ jobId?: string }>();
    const location = useLocation();

    const { unknownReadingsAllowed } = useSelector(applicationSelectors.getFeaturesConfig);
    const aiLiveAssistantEnabled = useSelector(applicationSelectors.isAiLiveAssistantEnabled);
    const searchValue = useSelector(searchSelectors.getSearchValue);

    const pendingJobs = useSelector(queueSelectors.getPendingJobIds);
    const jobsOpen = useSelector(jobsSelectors.getFilteredJobsOpen);
    const jobsDone = useSelector(jobsSelectors.getFilteredJobsDone);

    const pendingJobIdSet = useMemo(() => new Set(pendingJobs), [pendingJobs]);

    const isReadingRoute = location.pathname.includes('ablesung');
    const showAiAssistantEntry = aiLiveAssistantEnabled && isReadingRoute && isMobilePlatform;
    const showAddUnknownEntry = unknownReadingsAllowed && isReadingRoute;

    const aiAssistantNode = useMemo(
        () => (
            <JobListItem
                title={t('aiReading.buttons.startAssistant')}
                titleSize={'small'}
                icon={<Icon aria={{ 'aria-hidden': true }} className={'h-7 text-accent'} path={Icon.Path.mdiCreation} />}
                color={'accent'}
                onClick={() => {
                    // Pass the origin so the assistant's exit returns here (not to home).
                    dispatch(push('/ai-ablesung', { from: location.pathname }));
                }}
                iconRight={'ARROW'}
                aria={{
                    role: 'link',
                    'aria-label': t('aiReading.buttons.startAssistant'),
                    tabIndex: 0,
                }}
            />
        ),
        [t, dispatch, location.pathname],
    );

    const addUnknownNode = useMemo(
        () => (
            <JobListItem
                title={t('jobs.unknownMeterReading.listItemLabel')}
                titleSize={'small'}
                icon={<Icon aria={{ 'aria-hidden': true }} className={'h-7 text-accent'} path={Icon.Path.mdiPlus} />}
                color={'accent'}
                onClick={() => {
                    dispatch(push(`/ablesung/neu`));
                }}
                isActive={location.pathname === '/ablesung/neu'}
                iconRight={'ARROW'}
                aria={{
                    role: 'link',
                    'aria-label': t('jobs.unknownMeterReading.listItemLabel'),
                    tabIndex: 0,
                }}
            />
        ),
        [t, dispatch, location.pathname],
    );

    // Order mirrors the previous markup exactly: assistant entry, open jobs,
    // "add unknown reading", the "done" separator, then the done jobs.
    const rows = useMemo<JobRow[]>(() => {
        const result: JobRow[] = [];

        if (showAiAssistantEntry) {
            result.push({ kind: 'node', key: 'ai-assistant', height: COMPACT_ITEM_HEIGHT, node: aiAssistantNode });
        }

        for (const job of jobsOpen) {
            result.push({
                kind: 'job',
                key: job.id,
                job,
                iconRight: pendingJobIdSet.has(job.id) ? 'PENDING' : 'ARROW',
                aria: JOB_LINK_ARIA,
            });
        }

        if (showAddUnknownEntry) {
            result.push({ kind: 'node', key: 'add-unknown', height: COMPACT_ITEM_HEIGHT, node: addUnknownNode });
        }

        if (jobsDone.length > 0) {
            result.push({
                kind: 'separator',
                key: 'separator-done',
                text: t('jobs.labels.readingsDone'),
                aria: { role: 'heading', 'aria-level': 2 },
            });
            for (const job of jobsDone) {
                result.push({
                    kind: 'job',
                    key: job.id,
                    job,
                    isGhost: job.done,
                    isDone: job.done,
                    iconRight: pendingJobIdSet.has(job.id) ? 'PENDING' : 'CHECK',
                    aria: JOB_LINK_ARIA,
                });
            }
        }

        return result;
    }, [jobsOpen, jobsDone, pendingJobIdSet, showAiAssistantEntry, showAddUnknownEntry, aiAssistantNode, addUnknownNode, t]);

    return (
        <div
            className={classNames('job-list flex flex-col flex-1 gap-2', className)}
            id={'joblist'}
            role={'navigation'}
            aria-label={'Auftragsliste'}
        >
            <JobRowList rows={rows} routeBasePath={'/ablesung'} activeJobId={jobId} />

            {searchValue && searchValue.length > 0 && jobsOpen.length + jobsDone.length === 0 && (
                <div className={'bg-gray-200 p-4 rounded text-gray-700 text-sm text-center '}>
                    <Trans i18nKey={'jobs.search.noResults'} values={{ searchValue }} tOptions={{ interpolation: { escapeValue: true } }}>
                        Es konnten keine Ergebnisse für die Suche nach <br /> <b>"{searchValue}"</b> <br /> gefunden werden.
                    </Trans>
                    <br />
                </div>
            )}
        </div>
    );
};

export default JobList;
